import { F0Box, F0Heading, F0Text } from '@factorialco/f0-react'
import {
  WizardStep as DeprecatedWizardStep,
  WizardStepRenderParams as DeprecatedWizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { NetsuiteWizardData } from 'modules/integrations_netsuite/lib/types'

type NetSuiteWizardStep = DeprecatedWizardStep<NetsuiteWizardData> & {
  onSubmitSuccess?: (context: { companyId: string; accessId: string }) => void
}
type NetSuiteWizardStepRenderParams = DeprecatedWizardStepRenderParams<NetsuiteWizardData>

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <F0Box display="flex" flexDirection="column" gap="xs">
    <F0Text variant="description" color="secondary" content={label} />
    <F0Text variant="body" content={value || '-'} style={{ overflowWrap: 'anywhere' }} />
  </F0Box>
)

const VerifyStep = ({ data }: NetSuiteWizardStepRenderParams) => {
  return (
    <F0Box display="flex" flexDirection="row" gap="2xl">
      <F0Box display="flex" flexDirection="column" gap="xl" maxWidth="screen-sm">
        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Heading
            as="h2"
            variant="heading"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.title'
            )}
          />
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.description'
            )}
          />
        </F0Box>
        <F0Box
          display="flex"
          flexDirection="column"
          gap="xs"
          padding="lg"
          border="default"
          borderColor="info"
          borderRadius="md"
          background="info"
        >
          <F0Text
            variant="label"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.validation_title'
            )}
          />
          <F0Text
            variant="body"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.validation_description'
            )}
          />
        </F0Box>
      </F0Box>

      <F0Box width="96">
        <F0Box
          display="flex"
          flexDirection="column"
          gap="lg"
          padding="lg"
          background="secondary"
          borderRadius="md"
        >
          <F0Text
            variant="label"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.summary_title'
            )}
          />
          <F0Box
            display="flex"
            flexDirection="column"
            gap="lg"
            padding="lg"
            border="default"
            borderColor="secondary"
            borderRadius="md"
            background="primary"
          >
            <SummaryRow
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.account_id_label'
              )}
              value={data.answers.account_id || ''}
            />
            <SummaryRow
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.client_id_label'
              )}
              value={data.answers.client_id || ''}
            />
            <SummaryRow
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.certificate_id_label'
              )}
              value={data.answers.certificate_id || ''}
            />
          </F0Box>
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.summary_description'
            )}
          />
        </F0Box>
      </F0Box>
    </F0Box>
  )
}

export const verifyStep = (): NetSuiteWizardStep => ({
  title: i18n.t(
    'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.wizard_title'
  ),
  nextText: i18n.t(
    'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.finish_button'
  ),
  render: (props) => <VerifyStep {...props} />,
  isCompleted: (data) =>
    !!data.answers.account_id && !!data.answers.client_id && !!data.answers.certificate_id,
  onSubmitSuccess: ({ companyId, accessId }) => {
    track(trackingEvents.integrationsNetsuite.netsuiteSettingsConnectionVerified, {
      company_id: companyId,
      access_id: accessId,
    })
  },
})
